<script setup lang="ts">import { computed as _computed, ref as _ref } from 'vue';

import BotFooter from '@/components/BotFooter.vue'
import CatalogSlider from '@/components/CatalogSlider.vue'
import TopNav from '@/components/TopNav.vue'
import { products } from '@/data/products'
import { $tdb, langFromPath } from '@/lang'
import { BglVideo, Carousel } from '@bagelink/vue'
import { onMounted, watch } from 'vue'
import { useRoute } from 'vue-router'

const route = useRoute()
// const router = useRouter()
const lang = _computed(() => langFromPath(route.path))

const product = _computed(() => products.find((p) => {
	const slug = $tdb(p, 'slug', lang.value)
	return slug === route.params.product_slug
}))

function otherLanguages() {
	if (product.value) return undefined
	// for (const lang of availableLangs) {
	// 	const _u_dbProduct = products.find((p) => {
	// 		const slug = $tdb(p, 'slug', lang)
	// 		return slug === route.params.slug
	// 	})
	// }
}

const imageExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.webp']
const videoExtensions = ['.mp4', '.webm']

const filteredGallery = _computed(() => product.value?.imageGallery.filter(img => [...imageExtensions, ...videoExtensions].includes(img.extension)))

const productIndex = _computed(() => products.findIndex(p => p._id === product.value?._id))

function isVideo(image: any) {
	return videoExtensions.includes(image.extension)
}

let imgIndex = _ref(0)

const firstImageURL = _computed(() => product.value?.imageGallery.find(img => !isVideo(img))?.imageURL)
let canPlay = _ref(false)
function initVideo() {
	canPlay.value = false
	setTimeout(() => canPlay.value = true, 1000)
}

onMounted(() => {
	initVideo()
})

watch(
	() => product.value,
	(val) => {
		if (!val) otherLanguages()
		imgIndex.value = 0
		initVideo()
	},
)
</script>

<template>
	<div v-if="product">
		<TopNav />
		<div class="page-wrap">
			<div
				class="sold-wrap act_w1170 flex-desktop act_frame relative flex-start justify-content-space-between act_mt-4 act_mb-4 act_pb-0"
			>
				<div class="sold-txt-wrap">
					<h1 class="act_txt40 playfair balance act_no-marg act_pb-2">
						{{ $tdb(product, 'name', lang) }}
					</h1>
					<p class="sold-for">
						{{ $t('home.soldFor') }}:
					</p>
					<div class="playfair act_flex act_gap-025 act_blue act_no-marg align-items-flex-end act_pb-2">
						<p class="txt20 currency">
							€
						</p>
						<p class="act_txt30 act_no-marg">
							{{ product?.salePrice?.toLocaleString('en-US') }}
						</p>
					</div>
					<div class="blog-line act_mb-2 desktop" />
					<!-- MOBILE CAROUSEL -->
					<div class="sold-item-images act_pb-4 mobile act_mt-1">
						<Carousel v-model:index="imgIndex" :items="1" class="sold-item-main-img white-box">
							<div v-for="(img, i) in filteredGallery" :key="i">
								<BglVideo v-if="isVideo(img)" :key="img.imageURL" :status="imgIndex === i && canPlay ? 'play' : 'pause'" :src="img.imageURL" aspect-ratio="1:1" mute loop />
								<img v-else :src="img.imageURL" :alt="img.altText">
							</div>
						</Carousel>
						<div class="sold-item-gallery">
							<div v-for="(img, i) in filteredGallery" :key="img.imageURL" class="relative">
								<img v-if="isVideo(img)" class="play-btn" src="@/assets/play.png" alt="play">
								<img
									:class="{ activeImg: i === imgIndex }"
									class="sold-small-item-img hover white-box"
									:src="isVideo(img) ? firstImageURL : img.imageURL"
									:alt="img.altText"
									@click="imgIndex = i"
								>
							</div>
						</div>
					</div>
					<!-- END MOBILE CAROUSEL -->
					<div class="act_pb-2 article" v-html="$tdb(product, 'description', lang)" />
					<LocalLink class="act_btn relative sold-btn" to="/get-the-best-offer">
						{{ $t('home.getStarted') }}
					</LocalLink>
				</div>
				<!-- DESKTOP CAROUSEL -->
				<div class="sold-item-images act_pb-4 desktop">
					<Carousel v-model:index="imgIndex" :items="1" class="sold-item-main-img white-box">
						<div v-for="(img, i) in filteredGallery" :key="img.imageURL">
							<BglVideo v-if="isVideo(img)" :status="imgIndex === i && canPlay ? 'play' : 'pause'" :src="img.imageURL" aspect-ratio="1:1" mute loop />
							<img v-else :src="img.imageURL" :alt="img.altText">
						</div>
					</Carousel>
					<div class="flex gap-1 mt-2">
						<div v-for="(img, i) in filteredGallery" :key="img.imageURL" class="relative" style="max-width: 120px;">
							<img v-if="isVideo(img)" class="play-btn" src="@/assets/play.png" alt="play">
							<img
								:class="{ activeImg: i === imgIndex }"
								class="sold-small-item-img hover white-box"
								:src="isVideo(img) ? firstImageURL : img.imageURL"
								:alt="img.altText"
								@click="imgIndex = i"
							>
						</div>
					</div>
				</div>
				<!-- END DESKTOP CAROUSEL -->
			</div>
			<div class="act_pb-4" />
			<CatalogSlider :index="productIndex" />
		</div>

		<BotFooter />
	</div>
</template>

<style>
.play-btn{
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 50px;
  height: 50px;
  cursor: pointer;
  text-shadow: 0 0 10px 0 rgba(0, 0, 0, 0.5);
  user-select: none;
  pointer-events: none;
}
.activeImg {
	border: 2px solid var(--blue);
}

.sold-for {
	margin-bottom: -0.5rem;
}

.sold-item-images {
	width: 48%;
	margin-top: -6rem;
	margin-bottom: -6rem;
	position: sticky;
	top: 4rem;
}

.sold-item-main-img {
	aspect-ratio: 1.1/1;
	display: flex;
	align-items: center;
	justify-content: center;
}

.sold-item-gallery {
	display: grid;
	grid-template-columns: repeat(4, 1fr);
	gap: 1rem;
	margin-top: 1rem;
	position: relative;
	z-index: 2;
}

.sold-small-item-img {
	padding: 0.3rem !important;
	aspect-ratio: 1;
	object-fit: contain;
}

.sold-btn {
	bottom: -2rem;
}

.sold-txt-wrap {
	width: 48%;
}

@media screen and (max-width: 900px) {
	.sold-wrap {
		flex-direction: column-reverse;
		max-width: 500px;
		margin-left: auto;
		margin-right: auto;
		padding: 1rem;
	}

	.sold-item-images {
		width: unset;
		margin-top: 0rem;
		margin-bottom: 0rem;
		position: relative;
		transform: translateY(-4rem);
	}

	.sold-item-gallery {
		gap: 0.5rem;
		margin-top: 1rem;
	}

	.sold-small-item-img {
		padding: 0.125rem;
		border-radius: 14px;
	}

	.sold-txt-wrap {
		text-align: center;
		width: unset;
	}

	.sold-txt-wrap>div {
		justify-content: center;
	}

	.sold-wrap .blog-line {
		margin-left: auto;
		margin-right: auto;
	}
}
</style>
